import { Button, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { Link, useParams } from 'react-router';
import { TEHLIKE_SINIFI_ETIKET } from '../i18n/tr';
import { useAcilDurumOzeti, useCiktiIndir } from '../api/queries';
import { QueryStatus } from './parts';
import { Bolum, KayitDetayi } from '../sozlesme';
import { tamSayiGoster } from '../lib/format';
import {
  BelgelerBolumu,
  EkiplerBolumu,
  IletisimBolumu,
  TatbikatBolumu,
  ToplanmaBolumu,
} from './acil-durum-parts';

/**
 * ACİL DURUM — İŞYERİ SAYFASI (Kapsam §29; sözleşme P5 · P9 · P11).
 *
 * Bölümler alt alta: künye · belgeler (E1) · ekipler (E2 — görevlendirmeden)
 * · ekipman (E3 — envantere bağ) · toplanma noktaları (E4) · tatbikatlar (E5
 * — saha turu kabı) · acil iletişim (E6 + PDF). ⚠️ **DÜĞME GÖRÜNÜRLÜĞÜ
 * SUNUCUDAN** (`yetkiler.yazabilir` — §2.40). Ekran hiçbir sayıyı kendi
 * hesaplamaz: vade · eksik · sayı hepsi yanıtta gelir.
 */
export function AcilDurumIsyeriScreen(): React.JSX.Element {
  const { workplaceId } = useParams<{ workplaceId: string }>();
  const ozet = useAcilDurumOzeti(workplaceId);
  const indir = useCiktiIndir();
  if (!ozet.isSuccess) return <QueryStatus queries={[ozet]} kayit />;
  const o = ozet.data;
  return (
    <KayitDetayi
      testId="acil-durum-isyeri"
      geriYol="/acil-durum"
      geriEtiket="Acil durum"
      baslik={o.workplaceAd}
      eylemler={
        <Group gap="xs">
          <Button
            variant="default"
            data-testid="acil-durum-iletisim-pdf"
            loading={indir.isPending}
            onClick={() =>
              indir.mutate({
                yol: `/api/emergency/workplaces/${o.workplaceId}/contacts/pdf`,
                yedekAd: 'acil-iletisim.pdf',
              })
            }
          >
            Acil iletişim listesi PDF
          </Button>
          {/* ✏️ P13 · B1/4 — saha turu AÇMA kapısı (saha sorumlusu dahil); acil durum listesi DEĞİL. */}
          {o.yetkiler.tatbikatKapisi.yazabilir && (
            <Button
              data-testid="acil-durum-tatbikat-ac"
              component={Link}
              to={`/saha-turlari/yeni?tip=tatbikat&workplaceId=${o.workplaceId}`}
            >
              Tatbikat aç
            </Button>
          )}
        </Group>
      }
    >
      <Bolum baslik="Künye" testId="acil-durum-kunye">
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
          <Alan etiket="Şirket" deger={o.companyAd} />
          <Alan etiket="Tehlike sınıfı" deger={TEHLIKE_SINIFI_ETIKET[o.tehlikeSinifi]} />
          <Alan etiket="Çalışan sayısı" deger={tamSayiGoster(o.calisanSayisi)} />
          <Alan
            etiket="Acil durum ekipmanı (aktif)"
            testId="acil-durum-ekipman-sayisi"
            deger={
              <Link
                to={`/ekipman?workplaceId=${o.workplaceId}&acilDurum=true`}
                data-testid="acil-durum-ekipman-baglantisi"
              >
                {tamSayiGoster(o.acilDurumEkipmanSayisi)} kayıt — envanterde göster
              </Link>
            }
          />
        </SimpleGrid>
      </Bolum>
      <BelgelerBolumu ozet={o} />
      <EkiplerBolumu ozet={o} />
      <ToplanmaBolumu ozet={o} />
      <TatbikatBolumu ozet={o} />
      <IletisimBolumu ozet={o} />
    </KayitDetayi>
  );
}

function Alan({
  etiket,
  deger,
  testId,
}: {
  etiket: string;
  deger: React.ReactNode;
  testId?: string;
}): React.JSX.Element {
  return (
    <Stack gap={2} data-testid={testId}>
      <Text size="xs" c="dimmed">
        {etiket}
      </Text>
      {typeof deger === 'string' ? <Text size="sm">{deger}</Text> : deger}
    </Stack>
  );
}
